import { normalizeEntityType } from "../types";
import type { EntityType } from "../types";

/**
 * What a place has, rather than which steps it logs.
 *
 * Nobody thinks of a ride as "Entered Line, Main Line, At Lockers, On Ride,
 * Exit Ride". They think: it's a ride, and it has lockers, and there's a
 * preshow. The steps follow from that. So a type's default is a small core —
 * the steps every one of them has — plus named features that bring a step or
 * two with them, and an entity says which features it has.
 *
 * This isn't a guess about how the data is shaped; it's a description of it.
 * Of the 92 attractions carried over from the old database, 90 are exactly the
 * core plus some combination of these five features, and nobody had invented a
 * step outside the type's vocabulary. (The two that aren't: a ride with no
 * "Entered Line" at all, and a hotel pool filed as an attraction.)
 *
 * A feature is only ever a named group of step ids from the type's own default
 * list, so core + every feature *is* `DEFAULT_EVENT_OPTIONS[type]`, unchanged —
 * there's a test pinning that. Turning one off removes its steps and the edges
 * pointing at them; turning it on puts them back where the type default has
 * them. Custom steps someone adds by hand belong to no feature and are never
 * touched by either.
 */
export interface EntityFeature {
  id: string;
  /** Reads as something the place has: "Lockers", "A preshow". */
  label: string;
  /** What turning it on adds, said in terms of the logging. */
  hint: string;
  /** Ids of the steps it contributes, from the type's default options. */
  steps: string[];
}

export const ENTITY_FEATURES: Partial<Record<EntityType, EntityFeature[]>> = {
  ATTRACTION: [
    { id: "main-line", label: "A main line", hint: "the switchbacks after the entrance", steps: ["attr-main-line"] },
    { id: "indoor-queue", label: "An indoor queue", hint: "the queue goes into a building", steps: ["attr-inside"] },
    { id: "lockers", label: "Lockers", hint: "somewhere to stow your things", steps: ["attr-at-lockers"] },
    { id: "preshow", label: "A preshow", hint: "a room you watch something in", steps: ["attr-preshow-start", "attr-preshow-end"] },
    { id: "breakdowns", label: "Breakdowns", hint: "it stops often enough to be worth timing", steps: ["attr-delay-start", "attr-delay-end"] },
  ],
  EXPERIENCE: [
    { id: "check-in", label: "A check in", hint: "you report somewhere before it starts, like a tour or an excursion", steps: ["exp-check-in"] },
    { id: "indoor-venue", label: "An indoor venue", hint: "you go inside before it starts", steps: ["exp-inside"] },
    { id: "seating", label: "Seating", hint: "you're seated rather than standing", steps: ["exp-seated"] },
    { id: "preshow", label: "A preshow", hint: "something runs before the show itself", steps: ["exp-preshow-start", "exp-preshow-end"] },
    { id: "delays", label: "Delays", hint: "it starts late often enough to be worth timing", steps: ["exp-delay-start", "exp-delay-end"] },
  ],
  RESTAURANT: [
    { id: "indoor", label: "An inside", hint: "you go in rather than ordering at a window", steps: ["rest-inside"] },
    { id: "table-service", label: "Table service", hint: "someone seats you", steps: ["rest-seated"] },
  ],
  PARK: [
    { id: "security", label: "A security check", hint: "bag check on the way in", steps: ["park-at-security"] },
    { id: "gates", label: "Gates", hint: "arriving and getting through are separate", steps: ["park-inside"] },
  ],
  EVENT: [
    { id: "security", label: "A security check", hint: "bag check on the way in", steps: ["event-at-security"] },
    { id: "gates", label: "Gates", hint: "arriving and getting through are separate", steps: ["event-inside"] },
  ],
  HOTEL: [
    { id: "security", label: "A security check", hint: "a gate on the way onto the property", steps: ["hotel-at-security"] },
    { id: "front-desk", label: "A front desk", hint: "a check-in line worth timing", steps: ["hotel-check-in-line", "hotel-checked-in"] },
    { id: "bag-check", label: "Bag check", hint: "bags left with the desk", steps: ["hotel-checked-bags"] },
    { id: "room", label: "A room", hint: "coming and going from it over the stay", steps: ["hotel-in-room", "hotel-left-room"] },
  ],
  TRANSPORTATION: [
    { id: "check-in", label: "A check in", hint: "bag drop or a desk", steps: ["trans-check-in"] },
    { id: "security", label: "Security", hint: "a screening queue worth timing", steps: ["trans-security"] },
    { id: "boarding", label: "Boarding", hint: "boarding is separate from departing", steps: ["trans-boarding"] },
  ],
  AIRPORT: [
    { id: "check-in", label: "A check in", hint: "a desk or bag drop, and its line", steps: ["airport-line", "airport-checked-in", "airport-bag-drop"] },
    { id: "security", label: "Security", hint: "a screening queue worth timing", steps: ["airport-security-line", "airport-through-security"] },
    { id: "baggage-claim", label: "Baggage claim", hint: "waiting at the carousel after landing", steps: ["airport-baggage-claim", "airport-first-bag", "airport-received-bags"] },
  ],
  SHIP: [
    { id: "check-in", label: "A check in", hint: "the terminal desk", steps: ["ship-check-in"] },
    { id: "security", label: "Security", hint: "a screening queue worth timing", steps: ["ship-security"] },
    { id: "boarding", label: "Boarding", hint: "boarding is separate from sailing", steps: ["ship-boarding"] },
  ],
  LOCATION: [
    { id: "register", label: "A register queue", hint: "a checkout line worth timing, like a shop", steps: ["location-checking-out", "location-paid"] },
  ],
};

/** The features a type offers, or none for types whose steps are all core. */
export function featuresForType(type: EntityType | string | null | undefined): EntityFeature[] {
  const current = normalizeEntityType(type);
  return (current && ENTITY_FEATURES[current]) || [];
}
